import {
  toWSDateString,
  WSButton,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSList,
  WSSidebar,
  WSText
} from "@wingspanhq/fe-component-library";
import { Payable } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayables } from "@query";
import React from "react";
import { PAYABLE_EXPAND } from "../../../../Invoices/constants";
import { Card } from "../Card/Card";
import { RoutePropsFromRouteHelpers } from "../../../../shared/utils/createRouteHelpers";
import {
  PATH_PAYMENT_HISTORY,
  routeHelpersClientPayableDetails
} from "../../paths";
import { selectorClientPayableView } from "../../selectors/selectorClientPayableView";
import { ClientPayableAmountSection } from "./ClientPayableAmountSection";
import { ClientPayablePayeeInfo } from "./ClientPayablePayeeInfo";
import { ClientPayableStatusBadge } from "./ClientPayableStatusBadge";
import { useModalPayInvoice } from "./ModalPayInvoice";

type Props = RoutePropsFromRouteHelpers<
  typeof routeHelpersClientPayableDetails
>;

export const ClientPayableDetails: React.FC<Props> = props => {
  const payableId = props.match.params.payableId;
  const locationState = props.location.state as
    | { backPath?: string }
    | undefined;
  const backPath = locationState?.backPath || PATH_PAYMENT_HISTORY;

  const onClose = () => {
    props.history.push(backPath);
  };

  const queryPayable = queriesPayables.getPayable.useQuery({
    payableId,
    expand: PAYABLE_EXPAND
  });

  return (
    <WSSidebar.Container onClose={onClose}>
      {queryPayable.isLoading ? (
        <WSElement p="XL">
          <WSText>Loading...</WSText>
        </WSElement>
      ) : queryPayable.isError || !queryPayable.data ? (
        <WSElement p="XL">
          <WSEmptyState
            type="error"
            title="Something went wrong"
            description="We couldn't load this invoice."
            actions={{
              buttons: [
                { label: "Try again", onClick: () => queryPayable.refetch() }
              ]
            }}
          />
        </WSElement>
      ) : (
        <Loaded payable={queryPayable.data} />
      )}
    </WSSidebar.Container>
  );
};

type LoadedProps = {
  payable: Payable;
};

const Loaded: React.FC<LoadedProps> = props => {
  const view = selectorClientPayableView(props.payable);
  const modalPayInvoice = useModalPayInvoice();

  const onPay = () => {
    modalPayInvoice.open({
      payableId: view.id,
      payeeName: view.payeeName,
      amount: view.amount,
      currency: view.currency,
      preferredFundingSource: view.preferredFundingSource
    });
  };

  return (
    <WSSidebar.Layout
      header={view.externalId ? `Invoice #${view.externalId}` : "Invoice"}
    >
      <WSList gap="XL">
        <Card>
          <WSFlexBox wrap="nowrap" justify="space-between" alignItems="center">
            <ClientPayablePayeeInfo
              name={view.payeeName}
              email={view.payeeEmail}
            />
            <ClientPayableStatusBadge status={view.status} />
          </WSFlexBox>
        </Card>

        <Card>
          <WSText.Heading5 mb="M">
            {view.status === "paid" ? "Paid" : "Amount due"}
          </WSText.Heading5>
          <ClientPayableAmountSection
            amount={view.amount}
            currency={view.currency}
            lineItems={view.lineItems}
          />

          <WSElement mt="XL">
            <WSText.ParagraphSm color="gray500" mb="XS">
              {view.status === "paid" ? "Paid date" : "Due date"}
            </WSText.ParagraphSm>
            <WSText>
              {toWSDateString(
                view.status === "paid" ? view.paidAt : view.dueDate,
                "monthDayYear"
              )}
            </WSText>
          </WSElement>

          {view.notes && (
            <WSElement mt="XL">
              <WSText.ParagraphSm color="gray500" mb="XS">
                Notes
              </WSText.ParagraphSm>
              <WSText>{view.notes}</WSText>
            </WSElement>
          )}
        </Card>

        {view.canPay && (
          <Card>
            <WSButton fullWidth onClick={onPay}>
              Pay now
            </WSButton>
          </Card>
        )}
      </WSList>
    </WSSidebar.Layout>
  );
};
